/**
 * Wire shapes for the Settings screen.
 *
 * Mirrors `backend/src/game/account/account.types.ts` — READ FROM THERE, never
 * guessed. Dates arrive as ISO strings rather than `Date`: the server types
 * them `Date` because Prisma hands them over that way, and they are serialised
 * on the way out. Typing them `Date` here would compile and then throw the
 * first time anything called `.toLocaleDateString()` on a string.
 */

/** `User.role`. `PLAYER` is everyone; the other two open the admin panel. */
export type AccountRole = 'PLAYER' | 'ADMIN' | 'KYC_ADMIN';

/** `GET /account`. */
export interface AccountProfile {
  id: string;
  email: string;
  username: string | null;
  role: AccountRole;
  emailVerified: boolean;
  /**
   * Readable here, but NOT writable through `PATCH /account` — core owns both
   * at `PATCH /me/preferences`, where a locale is checked against the enabled
   * set and a currency against `CurrencyService`. Sending either to
   * `PATCH /account` is a 400 by design (the global pipe runs
   * `forbidNonWhitelisted`), so the form posts them to the other endpoint.
   */
  locale: string | null;
  currency: string | null;
  createdAt: string;

  /**
   * The billing address is core's (`core/payments/billing`) — the same columns
   * a deposit sends to the gateway and an invoice snapshots. `billingCompany`
   * is the game's own; core has no company field, only the invoice prints it.
   */
  firstName: string | null;
  lastName: string | null;
  billingLine1: string | null;
  billingLine2: string | null;
  billingCity: string | null;
  billingZip: string | null;
  /** Only asked for (and only checked) for US, CA and AU. */
  billingState: string | null;
  /** ISO 3166-1 alpha-2, uppercase. */
  billingCountry: string | null;
  billingCompany: string | null;

  /** Core's verdict: would a deposit accept this address, and if not, why. */
  billing: {
    complete: boolean;
    /** Core field names (`line1`, `zip`, …). */
    missing: string[];
    /** Present but refused — core field name → message. */
    invalid: Record<string, string>;
  };
}

/** The `PATCH /account` field names that make up the profile form. */
export const PROFILE_FIELDS = [
  'firstName', 'lastName',
  'billingLine1', 'billingLine2', 'billingCity', 'billingZip', 'billingState', 'billingCountry',
  'billingCompany',
] as const;

export type ProfileField = (typeof PROFILE_FIELDS)[number];

/** The subset of `AccountProfile` that `PATCH /account` accepts. */
export type AccountProfilePatch = Partial<Pick<AccountProfile, ProfileField>>;

/** One row of the Ledger tab. `amount` is signed — credits positive, debits negative. */
export interface AccountLedgerEntry {
  id: string;
  createdAt: string;
  entryType: string;
  /** SOFT is the game's Cash, PREMIUM its Gold. */
  currency: 'SOFT' | 'PREMIUM';
  amount: number;
  balanceAfter: number;
  description: string;
  /** Human rendering of `description` — never a slug. */
  label: string;
  itemKey: string | null;
  itemName: string | null;
  quantity: number | null;
}

export interface AccountLedgerPage {
  items: AccountLedgerEntry[];
  limit: number;
  offset: number;
  hasMore: boolean;
}

/** One row of the Invoices tab. */
export interface AccountInvoice {
  id: string;
  number: string;
  type: string;
  status: string;
  amountCents: number;
  vatRate: number;
  vatCents: number;
  totalCents: number;
  currency: string;
  description: string;
  /** The field core's own user-facing list drops — the reason this route exists. */
  paymentMethod: string | null;
  paidAt: string | null;
  createdAt: string;
}

export interface AccountInvoicePage {
  items: AccountInvoice[];
  page: number;
  pageSize: number;
  total: number;
}

/** `GET /i18n/config` — the enabled locales and currencies, from core. */
export interface I18nConfig {
  locales: { code: string; tag: string; label: string }[];
  defaultLocale: string;
  currencies: { code: string; symbol: string }[];
  defaultCurrency: string;
}

export type SettingsTab = 'PROFILE' | 'SOUND' | 'CONTROLS' | 'TUTORIAL' | 'BILLING' | 'LEDGER' | 'LEGAL';

export interface AccountState {
  profile: AccountProfile | null;
  i18n: I18nConfig | null;
  ledger: AccountLedgerPage | null;
  invoices: AccountInvoicePage | null;
  tab: SettingsTab;
  busy: boolean;
  error: string | null;
  /** One-line confirmation of the last successful write. */
  notice: string | null;
  /**
   * True once a password change has succeeded. Every refresh token was
   * revoked, the caller's own included, so the only honest next screen is the
   * login one — the panel says so rather than letting the player discover it
   * fifteen minutes later when the access token expires.
   */
  sessionEnded: boolean;
  /** Per-field billing errors, keyed by `ProfileField`; shown under each input. */
  billingErrors: Partial<Record<ProfileField, string>>;
}
